/**
 * Renders the floor plan / housing unit layout image shared by floorPlanDetail's
 * [data-secondary-panel] (desktop) and mobile image-expand screen, and housingUnitDetail's
 * inline [data-panel] content. Pure function — no DOM mounting, no event logic — matching this
 * folder's existing .dom.js convention (see floorPlanCards.dom.js, quickMoveInCards.dom.js).
 *
 * Uses the same `data-siteplan-card-image` attribute the card components use, so it's covered
 * for free by the existing broken-image fallback (handlers/imageErrorHandler.js).
 *
 * Also renders the zoom-in/zoom-out control markup for NHS20-8954 (`data-zoom-container`,
 * `data-zoom-image`, `data-zoom-controls`, `data-zoom-in`/`data-zoom-out`) — wired up by
 * floorPlanCarousel.dom.js's initializeFloorPlanCarousel() via handlers/imageZoomHandler.js.
 * Markup only lives here (declarative, matching this file's own convention); it starts hidden
 * and the handler reveals it once the image actually loads into the zoom canvas. Omitted
 * entirely when there's no image (the empty-state fallback), since there's nothing to zoom.
 *
 * @module nativeInteractiveSiteMap/components/floorPlanImage.dom
 */
import { zoomInIconSvg, zoomOutIconSvg } from 'constants/svg-assets';

const ZOOM_CONTROLS_CLASS = 'nhs-c-native-interactive-site-map__zoom-controls';
const ZOOM_BUTTON_CLASS = 'nhs-c-native-interactive-site-map__zoom-btn';
const ZOOM_DIVIDER_CLASS = 'nhs-c-native-interactive-site-map__zoom-divider';

/**
 * Derives the accessible alt text for a floor plan's layout image from its plan object. Floor
 * plan-specific (housingUnitDetail.plugin.js's inline image has its own, different fallback
 * text, since a housing unit isn't a plan) — shared only between floorPlanDetail.plugin.js's
 * secondary panel and its mobile floorPlanViewer.dom.js screen, which both show the same plan's
 * image and previously each carried their own identical copy of this function.
 * @param {object|null} plan
 * @returns {string}
 */
export function deriveFloorPlanImageAlt(plan) {
  return plan?.name ? `${plan.name} floor plan configuration image` : 'floor plan configuration image';
}

/**
 * @param {string} imageAlt
 * @returns {string} HTML string for the zoom in/out control pair.
 */
function renderZoomControls(imageAlt) {
  return `
    <div class="${ZOOM_CONTROLS_CLASS}" data-zoom-controls hidden data-qa="native_ism_floor_plan_zoom_controls">
      <button type="button"
              class="${ZOOM_BUTTON_CLASS}"
              aria-label="Zoom in on ${imageAlt}"
              data-zoom-in
              data-qa="native_ism_floor_plan_zoom_in">
        ${zoomInIconSvg}
      </button>
      <div class="${ZOOM_DIVIDER_CLASS}"></div>
      <button type="button"
              class="${ZOOM_BUTTON_CLASS}"
              aria-label="Zoom out on ${imageAlt}"
              data-zoom-out
              data-qa="native_ism_floor_plan_zoom_out">
        ${zoomOutIconSvg}
      </button>
    </div>
  `;
}

/**
 * @param {object} params
 * @param {string|null} params.imageUrl
 * @param {string} [params.imageAlt]
 * @returns {string} HTML string.
 */
export default function renderFloorPlanImage({ imageUrl, imageAlt = 'floor plan layout image' }) {
  return `
    <div class="nhs-u-height--full nhs-u-width--full" data-zoom-container data-qa="native_ism_floor_plan_image_wrapper">
      <img class="nhs-n1-u-object-fit--contain nhs-u-display--block nhs-u-height--full nhs-u-width--full"
           data-siteplan-card-image
           data-zoom-image
           loading="lazy"
           src="${imageUrl ?? ''}"
           alt="${imageAlt}"
           data-qa="native_ism_floor_plan_image" />
      ${imageUrl ? renderZoomControls(imageAlt) : ''}
    </div>
  `;
}
